import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import { eventApi } from "@circle-react/api";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { internalApi } from "@circle-react/helpers/urlHelpers/internalApi";
import type { Event } from "@circle-react/types/Event";
import { useToast } from "@circle-react-uikit/ToastV2";
import { ATTENDEE_ROLES } from "../utils";

const PER_PAGE = 10;

interface EventAttendeesResponse {
  count: number;
  has_next_page: boolean;
  page_count: number;
  records: any[];
  total_confirmed_record_count: number;
}

export const useEventAttendeesApi = (
  event: Event,
  role = ATTENDEE_ROLES.PARTICIPANT,
  perPage = PER_PAGE,
) => {
  const { space_id: spaceId, slug } = event || {};
  const { error, success } = useToast();
  const [page, setPage] = useState(1);
  const [isWaitingForAttendees, setIsWaitingForAttendees] = useState(false);

  const fetchNextPage = () => {
    setPage(page + 1);
  };
  const fetchPrevPage = () => {
    setPage(Math.max(page - 1, 1));
  };

  const resetPage = () => {
    setPage(1);
  };

  const {
    isLoading,
    isFetching,
    data,
    refetch,
    remove: removeAttendeesData,
  } = useQuery({
    queryKey: internalApi.eventAttendees.index({
      spaceId,
      slug,
      params: {
        page,
        role,
        per_page: perPage,
      },
    }),
    refetchInterval: isWaitingForAttendees ? 5000 : false,
    refetchIntervalInBackground: false,
    keepPreviousData: true,
    enabled: !!event,
  });

  const typedData = data as EventAttendeesResponse | undefined;
  const records = typedData?.records || [];
  const totalRecordCount = typedData?.count || 0;
  const totalConfirmedRecordCount = typedData?.total_confirmed_record_count;
  const pageRecordCount = typedData?.page_count;
  const pageCount = totalRecordCount
    ? Math.ceil(totalRecordCount / PER_PAGE)
    : 0;
  const hasPrevPage = page > 1;
  const hasNextPage = !!typedData?.has_next_page;
  const startRecordNumber = (page - 1) * PER_PAGE + 1;
  const endRecordNumber = startRecordNumber + (pageRecordCount ?? 0) - 1;

  // Mutation for adding attendees
  const { isLoading: isAdding, mutateAsync: addAttendeesAsync } = useMutation<
    unknown,
    ApiError,
    Record<string, unknown>
  >(params => eventApi.bulkRsvp({ event, params }), {
    onSuccess: () => {
      setPage(1);
      removeAttendeesData();
      setIsWaitingForAttendees(true);
      success(t("events.edit_modal.people.add_attendees_started"));
    },
    onError: () => {
      setIsWaitingForAttendees(false);
      error(t("events.edit_modal.people.add_attendees_error"));
    },
  });

  return {
    page,
    records,
    totalRecordCount,
    totalConfirmedRecordCount,
    pageRecordCount,
    pageCount,
    hasPrevPage,
    hasNextPage,
    startRecordNumber,
    endRecordNumber,
    isLoading,
    isFetching,
    isWaitingForAttendees,
    isAdding,
    addAttendeesAsync,
    refetch,
    fetchNextPage,
    fetchPrevPage,
    resetPage,
  };
};
